// src/components/SessionHistory.tsx - Session History Tab Component
import React, { useState, useEffect, useCallback } from 'react';
import { SessionPersistenceManager } from '../utils/SessionPersistenceManager';
import { Message } from '../types/Message';
import '../assets/styles/SessionHistory.css';

interface ConversationSession {
  sessionId: string;
  userId: string;
  messages: Message[];
  metadata: {
    startTime: number;
    lastActivity: number;
    deviceType: 'desktop' | 'mobile' | 'tablet';
    userAgent: string;
    title?: string;
  };
  state: {
    isStreamingResponse: boolean;
    activeProcesses: Record<string, boolean>;
    inputMode: 'text' | 'voice';
    voiceState: any;
  };
}

interface SessionHistoryProps {
  sessionManager: SessionPersistenceManager | null;
  currentSessionId: string | null;
  onSessionSelect: (sessionId: string) => void;
  onNewConversation: () => void;
  userId: string;
}

const SessionHistory: React.FC<SessionHistoryProps> = ({
  sessionManager,
  currentSessionId,
  onSessionSelect,
  onNewConversation,
  userId
}) => {
  const [sessions, setSessions] = useState<ConversationSession[]>([]);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  // Load sessions
  const loadSessions = useCallback(() => {
    if (sessionManager) {
      const userSessions = sessionManager.getUserSessions(userId);
      setSessions(userSessions);
    }
  }, [sessionManager, userId]);

  // Initial load and refresh on changes
  useEffect(() => {
    loadSessions();

    // Refresh periodically to catch new sessions
    const interval = setInterval(loadSessions, 5000);
    return () => clearInterval(interval);
  }, [loadSessions]);

  // Handle session selection
  const handleSelectSession = useCallback((sessionId: string) => {
    if (sessionId !== currentSessionId) {
      onSessionSelect(sessionId);
    }
  }, [currentSessionId, onSessionSelect]);

  // Handle session deletion
  const handleDeleteSession = useCallback((sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    if (confirmDelete === sessionId) {
      // Actually delete
      if (sessionManager) {
        sessionManager.deleteSession(sessionId);
        loadSessions();
        setConfirmDelete(null);
      }
    } else {
      // Ask for confirmation
      setConfirmDelete(sessionId);
      // Auto-clear confirmation after 3 seconds
      setTimeout(() => setConfirmDelete(null), 3000);
    }
  }, [confirmDelete, sessionManager, loadSessions]);

  // Format relative time
  const formatRelativeTime = (timestamp: number): string => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return new Date(timestamp).toLocaleDateString();
  };

  // Get preview text from session - shows LAST meaningful message for context
  const getPreviewText = (session: ConversationSession): string => {
    if (session.messages.length === 0) return 'No messages yet';

    // 🔥 Find last MEANINGFUL message (skip system/error messages)
    // Error messages often contain "Session not found", "Type: error", etc.
    const meaningfulMessages = session.messages.filter(msg => {
      // Skip system and error message types
      if (msg.type === 'error' || msg.type === 'system') return false;

      const content = (msg.content || '').toLowerCase();
      // Skip error messages and system notifications by content
      if (content.includes('type: error') ||
          content.includes('session not found') ||
          content.includes('error:') ||
          content.includes('❌') ||
          msg.sender === 'system') {
        return false;
      }
      // Skip empty or very short messages
      if (content.trim().length < 3) return false;
      return true;
    });

    if (meaningfulMessages.length === 0) {
      // Fallback to first user/assistant message if all filtered
      const anyMessage = session.messages.find(m => m.sender === 'user' || m.sender === 'assistant');
      if (anyMessage) {
        const content = anyMessage.content || '';
        const prefix = anyMessage.sender === 'user' ? 'You: ' : 'Max: ';
        return prefix + (content.length > 45 ? content.substring(0, 45) + '...' : content);
      }
      return 'No messages yet';
    }

    // Get the last meaningful message
    const lastMessage = meaningfulMessages[meaningfulMessages.length - 1];
    const content = lastMessage.content || '';
    const prefix = lastMessage.sender === 'user' ? 'You: ' : 'Max: ';

    // Truncate if too long
    const maxLength = 45;
    const truncatedContent = content.length > maxLength
      ? content.substring(0, maxLength) + '...'
      : content;

    return prefix + truncatedContent;
  };

  // Get session title
  const getSessionTitle = (session: ConversationSession): string => {
    if (session.metadata.title && session.metadata.title !== `Conversation ${new Date(session.metadata.startTime).toLocaleDateString()}`) {
      return session.metadata.title;
    }

    // Try to derive title from first user message
    const userMessages = session.messages.filter(m => m.sender === 'user');
    if (userMessages.length > 0) {
      const firstMsg = userMessages[0].content || '';
      if (firstMsg.length > 30) {
        return firstMsg.substring(0, 30) + '...';
      }
      return firstMsg || 'New conversation';
    }

    return 'New conversation';
  };

  return (
    <div className="tab-content session-history-content">
      <h3>Session History</h3>

      <button
        className="new-conversation-button"
        onClick={onNewConversation}
      >
        + New Conversation
      </button>

      <div className="sessions-list">
        {sessions.length === 0 ? (
          <div className="no-sessions">
            <p>No previous sessions found.</p>
            <p className="hint">Your conversations will appear here after you start chatting.</p>
          </div>
        ) : (
          sessions.map((session) => (
            <div
              key={session.sessionId}
              className={`session-item ${session.sessionId === currentSessionId ? 'active' : ''}`}
              onClick={() => handleSelectSession(session.sessionId)}
            >
              <div className="session-header">
                <span className="session-title">
                  {getSessionTitle(session)}
                </span>
                <span className="session-time">
                  {formatRelativeTime(session.metadata.lastActivity)}
                </span>
              </div>

              <div className="session-preview">
                {getPreviewText(session)}
              </div>

              <div className="session-footer">
                <span className="session-meta">
                  {session.messages.length} message{session.messages.length !== 1 ? 's' : ''}
                  {' '}&middot;{' '}
                  {session.metadata.deviceType}
                </span>

                <button
                  className={`delete-session-button ${confirmDelete === session.sessionId ? 'confirm' : ''}`}
                  onClick={(e) => handleDeleteSession(session.sessionId, e)}
                  title={confirmDelete === session.sessionId ? 'Click again to confirm' : 'Delete session'}
                >
                  {confirmDelete === session.sessionId ? 'Confirm?' : '×'}
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="session-history-footer">
        <p className="storage-info">
          Showing up to 5 most recent sessions
        </p>
      </div>
    </div>
  );
};

export default SessionHistory;
